import { defineProperty as _defineProperty, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { THEME_UI_DEFAULT_KEY } from '../../theme-provider/types.esm.js';
import { isFlatDesign } from '../../themes/const.esm.js';
import { borderToShadows } from '../../themes/theme-keys/shadows.esm.js';
import { transitions } from '../../themes/theme-keys/transitions.esm.js';
import { EMPTY_OBJECT } from '../../utils/object.esm.js';

var _ghost, _icon, _THEME_UI_DEFAULT_KEY;
var standardButtonDisabled = {
  pointerEvents: 'unset',
  outline: 'unset',
  boxShadow: 'unset',
  textShadow: 'unset',
  cursor: 'not-allowed',
  opacity: '0.4',
  filter: 'grayscale(100%)'
};
var standardButtonHover = function standardButtonHover(theme) {
  var isFlatDesign$1 = isFlatDesign(theme);
  return {
    opacity: isFlatDesign$1 ? '1' : '0.6'
  };
};
function standardActive(borderColor) {
  return _objectSpread2({
    boxShadow: 'unset',
    opacity: '1'
  }, borderColor ? {
    border: borderColor
  } : {});
}
function standardFocus(theme, borderColor) {
  var isFlatDesign$1 = isFlatDesign(theme);
  return _objectSpread2({
    zIndex: 2
  }, borderColor ? _objectSpread2({
    border: borderColor
  }, isFlatDesign$1 ? {} : {
    boxShadow: borderToShadows(borderColor)
  }) : {});
}
var standardStatic = {
  cursor: 'default'
};
var standardLoading = {
  '& #svg_spinner': {
    color: 'primary'
  }
};
var defaultControlStyles = {
  color: 'textSecondary',
  paddingY: 'md',
  paddingX: 'md',
  bg: 'neutral.light',
  borderTop: 'neutral.medium',
  borderRadius: undefined,
  '&:last-of-type:not(:focus)': {
    borderBottom: 'neutral.medium'
  }
};
var disabledControlStyles = {
  '&[disabled]': {
    opacity: '1',
    background: 'rgba(245, 245, 245, 0.4)',
    outline: 'unset',
    boxShadow: 'unset',
    textShadow: 'unset',
    cursor: 'not-allowed',
    filter: 'grayscale(100%)',
    '.csg-tui-icon': {
      opacity: '0.4'
    }
  }
};
var standardBehavior = function standardBehavior(theme, isStatic, isLoading, borderColor) {
  return !isStatic && !isLoading ? {
    cursor: 'pointer',
    '&:active:not(:disabled)': standardActive(borderColor),
    '&[disabled]': standardButtonDisabled,
    '&:focus:not(.disable-focus)': standardFocus(theme, borderColor),
    '&.is-toggled': standardFocus(theme, borderColor),
    '&.is-disabled': standardButtonDisabled,
    '&.is-hidden': {
      display: 'none'
    }
  } : _objectSpread2(_objectSpread2({}, standardStatic), isLoading ? standardLoading : {});
};
var baseButtonStyle = {
  borderRadius: 'default'
};
var getTransition = function getTransition() {
  var _t = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : transitions;
  var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : ['all'];
  return {
    '&:not([type="checkbox"]):not([type="radio"])': {
      transition: _t.create(props)
    }
  };
};
var greyHover = function greyHover(_t, props) {
  return _objectSpread2(_objectSpread2({
    color: 'textSecondary'
  }, getTransition(_t.transitions, ['background', 'transform'])), !props.isStatic && !props.isLoading ? {
    '&:hover:not(:disabled):not(.disable-hover)': {
      bg: 'neutral.lighter',
      color: 'text'
    }
  } : {});
};
var baseHover = function baseHover(_t, props) {
  return _objectSpread2(_objectSpread2({}, getTransition(_t.transitions, ['background', 'transform'])), !props.isStatic && !props.isLoading ? {
    '&:hover:not(:disabled):not(.is-toggled):not(.disable-hover)': {
      borderColor: 'black',
      opacity: '1' //overwriting - temp
    }
  } : {});
};
var primaryHover = function primaryHover(_t, props) {
  return _objectSpread2(_objectSpread2({
    color: 'text'
  }, getTransition(_t.transitions, 'background')), !props.isStatic && !props.isLoading ? {
    '&:hover:not(:disabled):not(.disable-hover)': {
      bg: 'primary',
      color: 'white'
    }
  } : {});
};
var ghostBase = function ghostBase(theme, props) {
  return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, baseButtonStyle), {}, {
    border: 'transparent',
    color: 'primary',
    bg: 'transparent'
  }, getTransition(theme.transitions, 'box-shadows')), !props.isStatic && !props.isLoading ? {
    cursor: 'pointer',
    '&:hover:not(:disabled):not(.disable-hover)': _objectSpread2({}, props.hoverColor ? {
      color: props.hoverColor
    } : standardButtonHover),
    '&:active:not(:disabled)': {
      textShadow: 'unset',
      opacity: '1'
    },
    '&:focus:not(.disable-focus)': _objectSpread2(_objectSpread2({}, standardFocus(theme)), {}, {
      textShadow: 'textPrimary'
    }),
    '&[disabled]': standardButtonDisabled,
    '&.is-disabled': standardButtonDisabled
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? standardLoading : {})), {}, {
    '&.is-selected': {
      bg: 'neutral.lighter'
    }
  });
};
var defaultBase = function defaultBase(theme) {
  var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : EMPTY_OBJECT;
  return _objectSpread2(_objectSpread2(_objectSpread2({}, baseButtonStyle), {}, {
    border: 'neutral.medium',
    color: 'text',
    bg: 'white-bg'
  }, getTransition(theme.transitions, 'box-shadows')), standardBehavior(theme, props.isStatic, props.isLoading, 'primary'));
};
var carouselButton = function carouselButton(_ref) {
  var isLeft = _ref.isLeft,
    removeHoverEffect = _ref.removeHoverEffect;
  return function (_t, props) {
    return _objectSpread2(_objectSpread2(_objectSpread2({}, ghostBase(_t, props)), {}, {
      color: 'white'
    }, getTransition(_t.transitions, ['background', 'transform'])), {}, {
      '&.is-hidden': {
        transform: "translateX(".concat(isLeft ? '-5.0' : '5.0', "rem)")
      }
    }, removeHoverEffect ? {} : {
      '&:hover': {
        bg: 'opaqueBlack.4',
        borderRadius: 'pill'
      }
    });
  };
};
var iconButton = function iconButton(_t) {
  return {
    '&:hover:not(:disabled):not(.disable-hover)': _objectSpread2(_objectSpread2({}, standardButtonHover), {}, {
      cursor: 'pointer'
    }),
    '&:focus:not(.disable-focus)': {
      zIndex: 2,
      color: 'primary'
    },
    '&[disabled]': _objectSpread2({}, standardButtonDisabled)
  };
};
var buttons = (_THEME_UI_DEFAULT_KEY = {}, _defineProperty(_THEME_UI_DEFAULT_KEY, THEME_UI_DEFAULT_KEY, defaultBase), _defineProperty(_THEME_UI_DEFAULT_KEY, "primary", function primary(theme, props) {
  return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, baseButtonStyle), {}, {
    border: props.isLoading ? 'secondary' : 'primary',
    color: 'white',
    bg: props.isLoading ? 'secondary' : 'primary'
  }, getTransition(theme.transitions, 'box-shadows')), !props.isStatic && !props.isLoading ? {
    cursor: 'pointer',
    //'&:hover:not(:disabled):not(.disable-hover)': 'primary.lighter',
    '&:hover:not(:disabled):not(.is-toggled):not(.disable-hover)': 'primary.lighter',
    standardButtonHover: standardButtonHover,
    '&:active:not(:disabled)': standardActive('primary'),
    '&[disabled]': _objectSpread2(_objectSpread2({}, standardButtonDisabled), {}, {
      bg: 'neutrals.15',
      border: 'lightGray'
    }),
    '&:focus:not(.disable-focus)': standardFocus(theme, 'primary'),
    '&.is-toggled': standardFocus(theme, 'primary')
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? {
    '& #svg_spinner': {
      color: 'white'
    }
  } : {})), {}, {
    '& .csg-tui-progress-spinner': {
      color: 'white'
    }
  });
}), _defineProperty(_THEME_UI_DEFAULT_KEY, "secondary", function secondary(theme, props) {
  return _objectSpread2(_objectSpread2(_objectSpread2({}, baseButtonStyle), {}, {
    border: 'primary',
    color: 'primary',
    bg: 'white-bg'
  }, getTransition(theme.transitions, 'box-shadows')), standardBehavior(theme, props.isStatic, props.isLoading, 'primary'));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, "tertiary", function tertiary(theme, props) {
  return _objectSpread2(_objectSpread2(_objectSpread2({}, baseButtonStyle), {}, {
    border: 'neutral.light',
    color: 'text',
    bg: 'white-bg'
  }, getTransition(theme.transitions, 'box-shadows')), !props.isStatic && !props.isLoading ? {
    cursor: 'pointer',
    '&:hover:not(:disabled):not(.disable-hover)': _objectSpread2(_objectSpread2({}, props.hoverColor ? {
      color: props.hoverColor
    } : {}), {}, {
      border: props.disabled ? 'neutral.light' : 'blackImportant'
    }),
    '&:active:not(:disabled)': {
      bg: 'neutral.light',
      textShadow: 'unset'
    },
    '&:focus:not(.disable-focus)': _objectSpread2(_objectSpread2({}, standardFocus(theme)), {}, {
      textShadow: 'textShadow'
    }),
    '&[disabled]': standardButtonDisabled,
    '&.is-toggled': standardFocus(theme, 'primary')
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? standardLoading : {}));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, "ghost", (_ghost = {}, _defineProperty(_ghost, THEME_UI_DEFAULT_KEY, ghostBase), _defineProperty(_ghost, "basic", function basic(theme, props) {
  return _objectSpread2(_objectSpread2({}, ghostBase(theme, props)), {}, {
    color: 'text'
  }, !props.isStatic && !props.isLoading ? {
    '&:focus:not(.disable-focus)': _objectSpread2({}, standardFocus(theme))
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? standardLoading : {}));
}), _ghost)), _defineProperty(_THEME_UI_DEFAULT_KEY, "ghostMod", function ghostMod(theme, props) {
  return _objectSpread2(_objectSpread2(_objectSpread2({}, ghostBase(theme, props)), greyHover(theme, props)), !props.isStatic && !props.isLoading ? {
    '&:focus:not(.disable-focus)': _objectSpread2(_objectSpread2({}, standardFocus(theme)), {}, {
      textShadow: 'textShadow'
    })
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? standardLoading : {}));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, "ghostPrimary", function ghostPrimary(theme, props) {
  return _objectSpread2(_objectSpread2(_objectSpread2({}, ghostBase(theme, props)), primaryHover(theme, props)), !props.isStatic && !props.isLoading ? {
    '&:active:not(:disabled)': {
      color: 'primaries.medium',
      textShadow: '0 0 3px #fff',
      opacity: '1'
    },
    '&:focus:not(.disable-focus)': _objectSpread2(_objectSpread2({}, standardFocus(theme)), {}, {
      textShadow: 'textShadow'
    }),
    '&.is-toggled': _objectSpread2({
      bg: 'primary',
      color: 'white'
    }, standardFocus(theme, 'primary'))
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? standardLoading : {}));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, "ghostDanger", function ghostDanger(theme, props) {
  return _objectSpread2(_objectSpread2({}, ghostBase(theme, props)), {}, {
    color: 'danger'
  }, !props.isStatic && !props.isLoading ? {
    '&:active:not(:disabled)': {
      color: 'danger',
      textShadow: '0 0 3px #fff',
      opacity: '1'
    },
    '&:focus:not(.disable-focus)': _objectSpread2(_objectSpread2({}, standardFocus(theme)), {}, {
      textShadow: 'textDanger'
    })
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? standardLoading : {}));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, "ghostWhite", function ghostWhite(theme, props) {
  return _objectSpread2(_objectSpread2({}, ghostBase(theme, props)), {}, {
    color: 'white'
  }, !props.isStatic && !props.isLoading ? {
    '&:active:not(:disabled)': {
      color: 'white',
      textShadow: '0 0 3px #fff',
      opacity: '1'
    },
    '&:focus:not(.disable-focus)': _objectSpread2(_objectSpread2({}, standardFocus(theme)), {}, {
      textShadow: 'unset'
    })
  } : _objectSpread2(_objectSpread2({}, standardStatic), props.isLoading ? standardLoading : {}));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, "listButton", function listButton(theme, props) {
  return _objectSpread2(_objectSpread2({}, defaultBase(theme, props)), greyHover(theme, props));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, 'carousel-left', carouselButton({
  isLeft: true
})), _defineProperty(_THEME_UI_DEFAULT_KEY, 'carousel-right', carouselButton({
  isLeft: false
})), _defineProperty(_THEME_UI_DEFAULT_KEY, 'new-carousel-chevron-left', carouselButton({
  isLeft: true,
  removeHoverEffect: true
})), _defineProperty(_THEME_UI_DEFAULT_KEY, 'new-carousel-chevron-right', carouselButton({
  isLeft: false,
  removeHoverEffect: true
})), _defineProperty(_THEME_UI_DEFAULT_KEY, "icon", (_icon = {}, _defineProperty(_icon, THEME_UI_DEFAULT_KEY, iconButton), _defineProperty(_icon, "clear", function clear(theme) {
  return _objectSpread2({
    color: 'neutral.dark'
  }, iconButton());
}), _defineProperty(_icon, 'no-borders', function noBorders(theme) {
  return _objectSpread2(_objectSpread2({}, iconButton()), {}, {
    border: 'none',
    '&:active:not(:disabled)': {
      border: 'none'
    }
  });
}), _icon)), _defineProperty(_THEME_UI_DEFAULT_KEY, "transferList", function transferList(theme, props) {
  return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, ghostBase(theme, props)), defaultControlStyles), disabledControlStyles), {}, {
    '&:active:not(:disabled)': {
      border: 'neutral.medium'
    },
    '&:focus:not(.disable-focus)': {
      boxShadow: '-0.1px 0.1px 4px #0054A6',
      borderColor: 'primary',
      zIndex: 2
    }
  });
}), _defineProperty(_THEME_UI_DEFAULT_KEY, 'theme-ui-select', function themeUiSelect(theme, props) {
  return _objectSpread2(_objectSpread2({}, defaultBase(theme, props)), baseHover(theme, props));
}), _defineProperty(_THEME_UI_DEFAULT_KEY, 'button-link', {
  color: 'primary',
  fontWeight: 'body',
  height: 'auto',
  '&:hover:not(.is-disabled):not(.is-toggled):not(.disable-hover), &:active:not(.is-disabled):not(.is-toggled):not(.disable-hover)': {
    textDecoration: 'underline',
    opacity: 1,
    cursor: 'pointer'
  },
  '&:visited': {
    color: 'primary'
  },
  '&:focus-visible': {
    outline: 'primary',
    outlineOffset: '0',
    borderRadius: 'default'
  },
  '&:active': {
    outline: 'none'
  },
  '&.is-disabled': {
    color: 'neutral.medium',
    cursor: 'default'
  }
}), _THEME_UI_DEFAULT_KEY);

export default buttons;
export { standardButtonDisabled, standardButtonHover };
//# sourceMappingURL=variants.esm.js.map
